import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { titleCase } from "../lib/utils";
import {
  Plus,
  Package,
  Clock,
  CheckCircle2,
  XCircle,
  Search,
  Check,
  X,
  FileText,
} from "lucide-react";
import {
  pendingComponentsApi,
  servicesApi,
  PendingComponent,
} from "../api/client";
import { formatRelativeTime } from "../lib/utils";

const statusConfig: Record<
  PendingComponent["status"],
  { label: string; icon: React.ReactNode; className: string }
> = {
  pending: {
    label: "Pending",
    icon: <Clock className="w-4 h-4" />,
    className: "bg-yellow-100 text-yellow-700",
  },
  verified: {
    label: "Verified",
    icon: <CheckCircle2 className="w-4 h-4" />,
    className: "bg-green-100 text-green-700",
  },
  rejected: {
    label: "Rejected",
    icon: <XCircle className="w-4 h-4" />,
    className: "bg-red-100 text-red-700",
  },
  registered: {
    label: "Registered",
    icon: <Check className="w-4 h-4" />,
    className: "bg-blue-100 text-blue-700",
  },
};

export default function PendingComponents() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [registeringId, setRegisteringId] = useState<string | null>(null);
  const [registerData, setRegisterData] = useState({
    service_id: "",
    component_type: "container",
  });

  const { data: componentsData, isLoading } = useQuery({
    queryKey: ["pending-components", statusFilter],
    queryFn: () =>
      pendingComponentsApi.list(
        statusFilter ? { status: statusFilter } : undefined,
      ),
  });

  const { data: servicesData } = useQuery({
    queryKey: ["services"],
    queryFn: () => servicesApi.list(),
  });

  const components = componentsData?.pending_components || [];
  const services = servicesData?.services || [];

  const createMutation = useMutation({
    mutationFn: pendingComponentsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-components"] });
      setShowForm(false);
    },
  });

  const verifyMutation = useMutation({
    mutationFn: pendingComponentsApi.verify,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-components"] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (id: string) => pendingComponentsApi.reject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-components"] });
    },
  });

  const registerMutation = useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: string;
      params: { service_id: string; component_type: string };
    }) => pendingComponentsApi.register(id, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-components"] });
      setRegisteringId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: pendingComponentsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-components"] });
    },
  });

  const handleCreateSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    createMutation.mutate({
      name: formData.get("name") as string,
      identifier: formData.get("identifier") as string,
      description: (formData.get("description") as string) || undefined,
    });
  };

  const handleRegister = (id: string) => {
    registerMutation.mutate({
      id,
      params: registerData,
    });
  };

  const filteredComponents = components.filter((component) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      component.name.toLowerCase().includes(query) ||
      component.identifier.toLowerCase().includes(query)
    );
  });

  const pendingCount = components.filter((c) => c.status === "pending").length;
  const verifiedCount = components.filter(
    (c) => c.status === "verified",
  ).length;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Pending Components
          </h1>
          <p className="text-gray-600 mt-1">
            Manage new components awaiting scan verification and registration
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          Add Component
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-900">
            {components.length}
          </div>
          <div className="text-sm text-gray-500">Total</div>
        </div>
        <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
          <div className="text-2xl font-bold text-yellow-700">
            {pendingCount}
          </div>
          <div className="text-sm text-yellow-600">Pending Verification</div>
        </div>
        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <div className="text-2xl font-bold text-green-700">
            {verifiedCount}
          </div>
          <div className="text-sm text-green-600">Verified</div>
        </div>
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
          <div className="text-2xl font-bold text-blue-700">
            {components.filter((c) => c.status === "registered").length}
          </div>
          <div className="text-sm text-blue-600">Registered</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search components..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>
          </div>
          <div className="w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input w-full"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
              <option value="registered">Registered</option>
            </select>
          </div>
        </div>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Add New Component
          </h2>
          <form onSubmit={handleCreateSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Name *</label>
                <input
                  type="text"
                  name="name"
                  className="input w-full"
                  placeholder="e.g., Payment Service"
                  required
                />
              </div>
              <div>
                <label className="label">Identifier *</label>
                <input
                  type="text"
                  name="identifier"
                  className="input w-full"
                  placeholder="e.g., payment-service, com.example.payment"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used to match against scan results
                </p>
              </div>
            </div>
            <div>
              <label className="label">Description</label>
              <textarea
                name="description"
                className="input w-full"
                rows={2}
                placeholder="Optional description..."
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="btn btn-primary"
              >
                Add Component
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Components List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      ) : filteredComponents.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No pending components
          </h3>
          <p className="text-gray-600 mb-4">
            {searchQuery || statusFilter
              ? "Try adjusting your filters"
              : "Add components to track new deployments"}
          </p>
          {!searchQuery && !statusFilter && (
            <button
              onClick={() => setShowForm(true)}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Component
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Component
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Identifier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredComponents.map((component) => {
                const status = statusConfig[component.status];
                const isRegistering = registeringId === component.id;

                return (
                  <tr key={component.id}>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <Package className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {component.name}
                          </div>
                          {component.description && (
                            <div className="text-sm text-gray-500">
                              {component.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                        {component.identifier}
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status.className}`}
                      >
                        {status.icon}
                        {status.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatRelativeTime(component.created_at)}
                      {component.created_by_name && (
                        <div className="text-xs text-gray-400">
                          by {component.created_by_name}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {isRegistering ? (
                        <div className="flex items-center justify-end gap-2">
                          <select
                            value={registerData.service_id}
                            onChange={(e) =>
                              setRegisterData((prev) => ({
                                ...prev,
                                service_id: e.target.value,
                              }))
                            }
                            className="input text-sm w-40"
                          >
                            <option value="">Select service</option>
                            {services.map((service) => (
                              <option key={service.id} value={service.id}>
                                {titleCase(service.name)}
                              </option>
                            ))}
                          </select>
                          <select
                            value={registerData.component_type}
                            onChange={(e) =>
                              setRegisterData((prev) => ({
                                ...prev,
                                component_type: e.target.value,
                              }))
                            }
                            className="input text-sm w-32"
                          >
                            <option value="container">Container</option>
                            <option value="library">Library</option>
                            <option value="config">Config</option>
                            <option value="other">Other</option>
                          </select>
                          <button
                            onClick={() => handleRegister(component.id)}
                            disabled={
                              !registerData.service_id ||
                              registerMutation.isPending
                            }
                            className="btn btn-primary btn-sm"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setRegisteringId(null)}
                            className="btn btn-secondary btn-sm"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          {component.status === "pending" && (
                            <>
                              <button
                                onClick={() =>
                                  verifyMutation.mutate(component.id)
                                }
                                disabled={verifyMutation.isPending}
                                className="btn btn-sm text-green-600 hover:bg-green-50"
                                title="Verify"
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  if (confirm("Reject this component?")) {
                                    rejectMutation.mutate(component.id);
                                  }
                                }}
                                disabled={rejectMutation.isPending}
                                className="btn btn-sm text-red-600 hover:bg-red-50"
                                title="Reject"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {component.status === "verified" && (
                            <button
                              onClick={() => {
                                setRegisteringId(component.id);
                                setRegisterData({
                                  service_id: "",
                                  component_type: "container",
                                });
                              }}
                              className="btn btn-primary btn-sm"
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              Register
                            </button>
                          )}
                          {(component.status === "pending" ||
                            component.status === "rejected") && (
                            <button
                              onClick={() => {
                                if (confirm("Delete this component?")) {
                                  deleteMutation.mutate(component.id);
                                }
                              }}
                              disabled={deleteMutation.isPending}
                              className="btn btn-sm text-gray-400 hover:text-red-600"
                              title="Delete"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
